/**
 * Purpose: Render the selected forecast-domain outline and outside-domain shading mask.
 * Assumptions: Domain grids come from domainConfig and projected grids require sampled edge traces via GridFactory.
 * External refs: src/external/weather-map-layer (vendored @openmeteo/weather-map-layer@0.0.19) GridFactory/GridData, react-map-gl/maplibre Source/Layer.
 */
import { GridFactory, type GridData } from "@openmeteo/weather-map-layer";
import type { FeatureCollection, Polygon } from "geojson";
import { useMemo } from "react";
import { Layer, Source, type LayerProps } from "react-map-gl/maplibre";
import { getForecastDomainById } from "../domainConfig";

const DOMAIN_OUTSIDE_LAYER: LayerProps = {
  id: "domain-outside",
  type: "fill",
  paint: {
    "fill-color": "#d1d5db",
    "fill-opacity": 0.45,
  },
};

const DOMAIN_OUTLINE_LAYER: LayerProps = {
  id: "domain-outline",
  type: "line",
  paint: {
    "line-color": "#000000",
    "line-width": 2,
  },
};

type DomainBoundOverlayProps = {
  visible: boolean;
  domainId: string;
  beforeId?: string;
};

const WORLD_RING: [number, number][] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

function normalizeLongitude(longitude: number) {
  return ((((longitude + 180) % 360) + 360) % 360) - 180;
}

function buildRegularGridRing(
  grid: Extract<GridData, { type: "regular" }>,
): [number, number][] {
  const lonMax = grid.lonMin + grid.dx * grid.nx;
  const latMax = grid.latMin + grid.dy * grid.ny;
  const west = Math.min(grid.lonMin, lonMax);
  const east = Math.max(grid.lonMin, lonMax);
  const south = Math.min(grid.latMin, latMax);
  const north = Math.max(grid.latMin, latMax);

  return [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];
}

function getRingSegment(
  grid: GridData,
  yStart: number,
  yEnd: number,
  xStart: number,
  xEnd: number,
) {
  const points: [number, number][] = [];
  const rangeGrid = GridFactory.create(grid, [
    { start: yStart, end: yEnd },
    { start: xStart, end: xEnd },
  ]);

  rangeGrid.forEachPoint((point) => {
    points.push([normalizeLongitude(point.lon), point.lat]);
  });

  return points;
}

function buildDomainRing(grid: GridData): [number, number][] {
  if (grid.type === "regular") {
    return buildRegularGridRing(grid);
  }

  if (
    grid.type !== "projectedFromBounds" &&
    grid.type !== "projectedFromGeographicOrigin" &&
    grid.type !== "projectedFromProjectedOrigin"
  ) {
    const [west, south, east, north] = GridFactory.create(grid).getBounds();
    return [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ];
  }

  const left = getRingSegment(grid, 0, grid.ny, 0, 1);
  const top = getRingSegment(grid, grid.ny - 1, grid.ny, 0, grid.nx);
  const right = getRingSegment(
    grid,
    0,
    grid.ny,
    grid.nx - 1,
    grid.nx,
  ).reverse();
  const bottom = getRingSegment(grid, 0, 1, 0, grid.nx).reverse();
  const ring = [
    ...left,
    ...top.slice(1),
    ...right.slice(1),
    ...bottom.slice(1),
  ];

  if (ring.length > 0) {
    ring.push(ring[0]);
  }

  return ring;
}

function buildDomainMaskGeoJson(grid: GridData): FeatureCollection<Polygon> {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [WORLD_RING, buildDomainRing(grid)],
        },
      },
    ],
  };
}

function buildDomainGeoJson(grid: GridData): FeatureCollection<Polygon> {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [buildDomainRing(grid)],
        },
      },
    ],
  };
}

export function DomainBoundOverlay({
  visible,
  domainId,
  beforeId,
}: DomainBoundOverlayProps) {
  const domain = useMemo(() => getForecastDomainById(domainId), [domainId]);
  const domainMaskGeoJson = useMemo(
    () => buildDomainMaskGeoJson(domain.grid),
    [domain.grid],
  );
  const domainGeoJson = useMemo(
    () => buildDomainGeoJson(domain.grid),
    [domain.grid],
  );

  if (!visible) {
    return null;
  }

  return (
    <>
      <Source
        id="domain-outside-source"
        type="geojson"
        data={domainMaskGeoJson}
      >
        <Layer {...DOMAIN_OUTSIDE_LAYER} beforeId={beforeId} />
      </Source>
      <Source id="domain-outline-source" type="geojson" data={domainGeoJson}>
        <Layer {...DOMAIN_OUTLINE_LAYER} beforeId={beforeId} />
      </Source>
    </>
  );
}
